/**
 * Accordion 手风琴组件
 * 支持单个或多个展开模式
 * 
 * @example
 * import { initAccordion } from './components/accordion.js';
 * initAccordion('.accordion', { multiple: false, defaultOpen: 0 });
 */
export function initAccordion(accordionSelector, options = {}) {
  const {
    multiple = false, // 是否允许多个同时展开
    defaultOpen = 0, // 默认展开的索引（-1 表示不展开任何项）
  } = options;

  const accordions = typeof accordionSelector === 'string'
    ? Array.from(document.querySelectorAll(accordionSelector))
    : [accordionSelector].filter(Boolean);

  if (accordions.length === 0) {
    console.warn(`Accordion: No accordion found for selector "${accordionSelector}"`);
    return [];
  }

  return accordions.map((accordion) => {
    const items = Array.from(accordion.querySelectorAll('.accordion-item'));
    if (!items.length) return null;

    // 检查是否已有展开项
    const alreadyOpen = items.some((item) => item.classList.contains('is-open'));
    
    // 如果没有展开项且指定了默认展开，则展开第一项或指定项
    if (!alreadyOpen && defaultOpen >= 0) {
      const targetIndex = Math.min(defaultOpen, items.length - 1);
      items[targetIndex]?.classList.add('is-open');
    }

    // 初始化每个面板的高度
    items.forEach((item) => {
      const panel = item.querySelector('.accordion-panel');
      const header = item.querySelector('.accordion-header');
      if (!panel || !header) return;

      const isOpen = item.classList.contains('is-open');
      
      if (isOpen) {
        panel.style.maxHeight = panel.scrollHeight + 'px';
        header.setAttribute('aria-expanded', 'true');
      } else {
        panel.style.maxHeight = '0px';
        header.setAttribute('aria-expanded', 'false');
      }
    });

    // 绑定点击事件
    items.forEach((item) => {
      const header = item.querySelector('.accordion-header');
      const panel = item.querySelector('.accordion-panel');
      if (!header || !panel) return;

      header.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        // 如果不允许多个展开，先收起所有项
        if (!multiple) {
          items.forEach((other) => {
            const otherPanel = other.querySelector('.accordion-panel');
            const otherHeader = other.querySelector('.accordion-header');
            if (!otherPanel || !otherHeader) return;

            other.classList.remove('is-open');
            otherPanel.style.maxHeight = '0px';
            otherHeader.setAttribute('aria-expanded', 'false');
          });
        }

        // 切换当前项
        if (!isOpen) {
          item.classList.add('is-open');
          panel.style.maxHeight = panel.scrollHeight + 'px';
          header.setAttribute('aria-expanded', 'true');
        } else if (multiple) {
          // 如果允许多个展开，可以关闭当前项
          item.classList.remove('is-open');
          panel.style.maxHeight = '0px';
          header.setAttribute('aria-expanded', 'false');
        }
        // 如果不允许多个展开且当前项已打开，则什么都不做（保持打开状态）
      });
    });

    // 返回控制对象
    return {
      open: (index) => {
        const item = items[index];
        if (!item) return;
        const panel = item.querySelector('.accordion-panel');
        const header = item.querySelector('.accordion-header');
        if (!panel || !header) return;

        if (!multiple) {
          items.forEach((other) => {
            const otherPanel = other.querySelector('.accordion-panel');
            const otherHeader = other.querySelector('.accordion-header');
            if (otherPanel && otherHeader) {
              other.classList.remove('is-open');
              otherPanel.style.maxHeight = '0px';
              otherHeader.setAttribute('aria-expanded', 'false');
            }
          });
        }

        item.classList.add('is-open');
        panel.style.maxHeight = panel.scrollHeight + 'px';
        header.setAttribute('aria-expanded', 'true');
      },
      close: (index) => {
        const item = items[index];
        if (!item) return;
        const panel = item.querySelector('.accordion-panel');
        const header = item.querySelector('.accordion-header');
        if (!panel || !header) return;

        item.classList.remove('is-open');
        panel.style.maxHeight = '0px';
        header.setAttribute('aria-expanded', 'false');
      },
      toggle: (index) => {
        const item = items[index];
        if (!item) return;
        const isOpen = item.classList.contains('is-open');
        if (isOpen) {
          this.close(index);
        } else {
          this.open(index);
        }
      },
      getOpenIndexes: () => {
        return items
          .map((item, index) => (item.classList.contains('is-open') ? index : -1))
          .filter((index) => index !== -1);
      },
    };
  }).filter(Boolean);
}
